import React, { useEffect, useState, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

const QuienSoySection = () => {
  const data = [
    {
      percentage: 100,
      title: "Experto en SST",
      description:
        "Más de dos décadas liderando proyectos en múltiples industrias con cumplimiento de normativas locales e internacionales.",
    },
    {
      percentage: 100,
      title: "Asesor y Formador",
      description:
        "He formado a cientos de profesionales HSE con herramientas prácticas y estrategias innovadoras para la gestión de riesgos.",
    },
    {
      percentage: 100,
      title: "Visionario",
      description:
        "La SST como ventaja competitiva: cultura, datos y mejora continua con soporte directivo.",
    },
    {
      percentage: 100,
      title: "Experto en IA aplicada en SST",
      description:
        "Integro inteligencia artificial en seguridad y salud en el trabajo: análisis predictivo, automatización de informes e inspecciones con visión por computador.",
    },
  ];

  const tags = [
    { text: "IA aplicada en SST", to: "/ia-aplicada-en-sst" },
    { text: "Cómo usar ChatGPT para SST", to: "/chatgpt-sst" },
    { text: "Prompts ChatGPT para prevencionistas", to: "/prompts-chatgpt-prevencionistas" },
    { text: "Automatizar informes SST con IA", to: "/automatizar-informes-sst-ia" },
    { text: "IA para inspecciones de seguridad", to: "/inspecciones-seguridad-ia" },
    { text: "Herramientas IA para SST", to: "/herramientas-ia-sst" },
    { text: "IA productividad en SST", to: "/productividad-ia-sst" },
    { text: "IA análisis de riesgos laborales", to: "/ia-analisis-riesgos-laborales" },
  ];

const [visibleItems, setVisibleItems] = useState([]); 
const sectionRef = useRef(null);

useEffect(() => {
  const sectionNode = sectionRef.current;

  if (!sectionNode) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        setVisibleItems((prev) => {
          const i = parseInt(entry.target.dataset.index || "0", 10);
          return [...new Set([...prev, i])];
        });
      }
    });
  }, { threshold: 0.5 });

  const items = sectionNode.querySelectorAll(".animated-item");
  items.forEach((item) => observer.observe(item));

  return () => {
    items.forEach((item) => observer.unobserve(item));
  };
}, []);


  // Schema.org: página "Sobre mí" con foco en IA aplicada en SST
  const aboutSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "¿Quién soy? — Ernesto Mora",
    "description":
      "Ernesto Mora, experto en IA aplicada en SST: ChatGPT para seguridad laboral, automatización de informes, inspecciones con IA y productividad HSEQ.",
    "url": "https://ernestomora.com/quien-soy",
    "about": {
      "@type": "Person",
      "name": "Ernesto Mora",
      "jobTitle": "Speaker y Consultor en IA aplicada en SST",
      "affiliation": { "@type": "Organization", "name": "ESGRYMA Consulting Group" }
    }
  };

  return (
    <section
      ref={sectionRef}
      id="quien-soy"
      className="py-12 bg-black text-white"
      aria-labelledby="heading-quien-soy"
    >
      {/* SEO para esta sección/ruta */}
      <Helmet>
        <title>¿Quién soy? | IA aplicada en SST, ChatGPT y automatización — Ernesto Mora</title>
        <meta
          name="description"
          content="Conoce a Ernesto Mora: experto en IA aplicada en SST. Uso de ChatGPT para prevención, automatizar informes, inspecciones con IA y aumento de productividad para profesionales HSEQ."
        />
        <meta
          name="keywords"
          content="IA aplicada en SST, ChatGPT SST, prompts ChatGPT prevencionistas, automatizar informes SST con IA, IA inspecciones seguridad, IA análisis riesgos laborales, herramientas IA para SST, IA productividad SST, inteligencia artificial seguridad laboral, profesionales HSEQ"
        />
        <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      </Helmet>

      <div className="max-w-6xl mx-auto px-4 text-center">
        {/* Mantén H2 para no duplicar H1 de la página */}
        <h2 id="heading-quien-soy" className="text-7xl font-bold mb-4 text-[#FFD700]">
          ¿Quién Soy?
        </h2>

        {/* Párrafo SEO con la keyword principal y variantes */}
        <p className="text-gray-300 max-w-3xl mx-auto mb-10 text-lg">
          Soy <strong>Ernesto Mora</strong>, consultor y speaker en{" "}
          <strong>IA aplicada en SST</strong>. Ayudo a líderes HSE a implementar{" "}
          <em>ChatGPT en seguridad laboral</em>,{" "}
          <em>automatizar informes SST con IA</em>, y ejecutar{" "}
          <em>inspecciones de seguridad con visión por computador</em> para elevar
          la <strong>productividad</strong> y reducir incidentes.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {data.map((item, index) => (
            <div
              key={index}
              className={`flex flex-col items-center transition-opacity duration-700 ease-out ${
                visibleItems.includes(index) ? "opacity-100" : "opacity-0"
              } animated-item`}
              data-index={index}
            >
              <div className="relative mb-4" aria-hidden="true">
                <svg
                  width="200"
                  height="200"
                  viewBox="0 0 140 140"
                  className="transform -rotate-90"
                  role="img"
                  aria-label={`${item.title} ${item.percentage}%`}
                >
                  <circle cx="70" cy="70" r="65" stroke="#333" strokeWidth="15" fill="none" />
                  <circle
                    cx="70"
                    cy="70"
                    r="65"
                    stroke="#FFD700"
                    strokeWidth="10"
                    fill="none"
                    strokeDasharray="408.4"
                    strokeDashoffset={
                      visibleItems.includes(index) ? (1 - item.percentage / 100) * 408.4 : 408.4
                    }
                    style={{
                      strokeLinecap: "round",
                      transition: "stroke-dashoffset 2s ease-in-out, stroke 0.5s ease",
                    }}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-3xl font-bold">{item.percentage}%</span>
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
              <p className="text-gray-300 text-center">{item.description}</p>
            </div>
          ))}
        </div>

        {/* Tags internos (pasan autoridad y refuerzan intención de búsqueda) */}
        <ul className="flex flex-wrap gap-2 justify-center mt-10">
          {tags.map((t) => (
            <li key={t.to}>
              <Link
                to={t.to}
                className="px-3 py-1 rounded-full border border-[#FFD700]/30 text-sm text-gray-200 hover:text-black hover:bg-[#FFD700] transition"
                aria-label={`Leer más sobre ${t.text}`}
              >
                #{t.text}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default QuienSoySection;
